import Modal from "../layout/Modal";
import { formatDateShortMonth, formatAmount } from "@utils";
import { TruckIcon, CalendarIcon, WrenchIcon } from "@heroicons/react/16/solid";

const formatDeliveryAddress = (address) => {
  if (!address) return null;
  return [address.line1, address.city, address.postcode].filter(Boolean).join(", ");
};

export const InvoiceDetailsModal = ({ invoice, isOpen, onClose, orgId }) => {
  const shouldShowVatDetails = orgId !== "peppard";
  const deliveryAddress = formatDeliveryAddress(invoice?.deliveryAddress);

  return (
    <Modal
      isOpen={!!invoice ? isOpen : false}
      onClose={onClose}
      size="md"
      title={invoice?.number}
      bodyClassName="flex flex-col"
    >
      <div className="shrink-0 px-5 py-4 bg-white border-b border-zinc-200">
        <div className="flex flex-col gap-3 text-xs/5 font-light">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5">
              <CalendarIcon className="size-4" />
              <span>{formatDateShortMonth(invoice?.dueDate)}</span>
            </span>
            {invoice?.customerReference && (
              <span className="flex items-center gap-1.5">
                <WrenchIcon className="size-4" />
                <span>{invoice.customerReference}</span>
              </span>
            )}
          </div>
          {deliveryAddress && (
            <span className="flex items-center gap-1.5">
              <TruckIcon className="size-4" />
              <span>{deliveryAddress}</span>
            </span>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-5 bg-zinc-50">
        <div className="flex flex-col gap-8 sm:gap-4">
          {invoice?.invoiceLineItems?.map((lineItem, idx) => (
            <div key={idx} className="flex gap-3 items-start">
              <span className="shrink-0 w-8 text-xs/6 font-bold">{lineItem.quantity}x</span>
              <div className="flex flex-col gap-2 min-w-0">
                <span className="text-xs/6 font-bold break-words">{lineItem.description}</span>
                <span className="text-xs/5 font-light">
                  {formatAmount(lineItem.netUnitPrice)} each
                  {shouldShowVatDetails && (
                    <>
                      &nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;
                      {formatAmount(lineItem.vatAmount)} Total VAT{" "}
                      {lineItem.vatRate * 100}%
                    </>
                  )}
                </span>
              </div>
              <span className="ml-auto text-xs/6 font-semibold">
                {formatAmount(lineItem.netAmount)}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="shrink-0 flex flex-col gap-2 p-4 bg-white border-t border-zinc-200 shadow-[0_-6px_10px_-6px_rgba(0,0,0,0.08)]">
        <div className="flex justify-between items-center font-light text-zinc-700">
          <span className="text-xs/7">Net total</span>
          <span className="text-xs/7">{formatAmount(invoice?.netAmount)}</span>
        </div>
        <div className="flex justify-between items-center font-light text-zinc-700">
          <span className="text-xs/7">VAT total</span>
          <span className="text-xs/7">{formatAmount(invoice?.vatAmount)}</span>
        </div>
        <div className="flex justify-between items-center font-semibold pt-4 border-t border-zinc-600">
          <span className="text-sm/6">Gross invoice total</span>
          <span className="text-sm/6">{formatAmount(invoice?.grossAmount)}</span>
        </div>
      </div>
    </Modal>
  );
};

export default InvoiceDetailsModal;
